import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardTitle } from '@auctisync/ui';
import { Layout } from '../../components/Layout';
import { PageHead } from '../../components/PageHead';
import {
  fetchHelpArticles,
  fetchHelpCategory,
  type HelpArticleSummary,
  type HelpCategory,
} from '@auctisync/api-client';
import { useTenant } from '../../contexts/TenantContext';
import { supabase } from '../../lib/supabase';

export function Category() {
  const tenant = useTenant();
  const { slug = '' } = useParams<{ slug: string }>();
  const [category, setCategory] = useState<HelpCategory | null>(null);
  const [articles, setArticles] = useState<HelpArticleSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function load() {
      if (!supabase) {
        setError('Help Center backend is warming up. Try again in a moment.');
        return;
      }
      // In white-label mode the tenant's category wins over the platform one on
      // the same slug — shared with every other help surface.
      const tenantId = tenant?.id ?? null;
      const cat = await fetchHelpCategory(supabase, slug, tenantId);
      if (cat.error || !cat.category) {
        setError('Category not found.');
        return;
      }
      setCategory(cat.category);
      const arts = await fetchHelpArticles(supabase, {
        tenantId,
        categoryId: cat.category.id,
        order: 'newest',
      });
      if (arts.error) {
        setError('Could not load articles in this category.');
        return;
      }
      setArticles(arts.articles);
    }
    void load();
  }, [slug, tenant]);

  return (
    <Layout>
      <PageHead
        title={
          category
            ? `${category.title} — AuctiSync Help Center`
            : 'Category — AuctiSync Help Center'
        }
        description={category?.description ?? 'Help articles in this category.'}
        canonicalPath={`/c/${slug}`}
      />
      <section className="mx-auto max-w-5xl px-6 pt-12 pb-8">
        <a
          href="/"
          className="text-sm underline-offset-4 hover:underline"
          style={{ color: 'var(--brand-accent)' }}
        >
          ← All categories
        </a>
        {category ? (
          <>
            <h1
              className="mt-4 text-3xl font-bold tracking-tight md:text-4xl"
              style={{ color: 'var(--text)' }}
            >
              {category.title}
            </h1>
            {category.description ? (
              <p className="mt-3 max-w-2xl text-lg" style={{ color: 'var(--text-muted)' }}>
                {category.description}
              </p>
            ) : null}
          </>
        ) : null}
      </section>

      <section className="mx-auto max-w-5xl px-6 pb-16">
        {error ? (
          <p style={{ color: 'var(--semantic-error)' }}>{error}</p>
        ) : !articles ? (
          <p style={{ color: 'var(--text-muted)' }}>Loading…</p>
        ) : articles.length === 0 ? (
          <EmptyCategory />
        ) : (
          <div className="grid gap-4">
            {articles.map((a) => (
              <a key={a.id} href={`/a/${a.slug}`} className="group">
                <Card className="transition-transform hover:-translate-y-0.5">
                  <CardContent className="flex flex-col gap-2 p-6">
                    <CardTitle className="text-lg">{a.title}</CardTitle>
                    <CardDescription>{a.summary}</CardDescription>
                  </CardContent>
                </Card>
              </a>
            ))}
          </div>
        )}
      </section>
    </Layout>
  );
}

function EmptyCategory() {
  return (
    <div
      className="rounded-lg border p-8 text-center"
      style={{ borderColor: 'var(--border)', background: 'var(--surface-muted)' }}
    >
      <p className="text-lg" style={{ color: 'var(--text)' }}>
        No articles here yet.
      </p>
      <p className="mt-2 text-sm" style={{ color: 'var(--text-muted)' }}>
        Articles ship with every feature module per the docs-as-DoD discipline. This category will
        fill in as the matching modules roll out.
      </p>
    </div>
  );
}
